/**
 * Quick estimate: 4 questions on one screen → result
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, CardContent } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Page, PageHeader, StickyActions } from '../ui';
import HouseBasicsFields, { isBasicsValid } from './HouseBasicsFields';
import { getEstimateCopy } from './copy';
import { useLanguage } from '../../utils/LanguageContext';
import { useEstimate } from '../../contexts/EstimateContext';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import UsageAlertBanner from '../gate/UsageAlertBanner';
import { calculateQuickEstimate } from '../../utils/estimateEngine';

export const DEFAULT_QUICK_DRAFT = {
  houseType: 'terrace',
  builtUpArea: '',
  floors: 1,
  finishingLevel: 'standard',
  projectName: '',
};

export const pickBasics = (inputs = {}) => ({
  houseType: inputs.houseType || DEFAULT_QUICK_DRAFT.houseType,
  builtUpArea: inputs.builtUpArea ? String(inputs.builtUpArea) : '',
  floors: Number(inputs.floors) || 1,
  finishingLevel: inputs.finishingLevel || DEFAULT_QUICK_DRAFT.finishingLevel,
  projectName: inputs.projectName || '',
});

const QuickEstimatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = getEstimateCopy(language);
  const { quickDraft, setQuickDraft, result, setResult, setCompare } = useEstimate();
  const { checkAccess, GateModalComponent } = useGate();

  const compareMode = searchParams.get('compare') === '1' && !!result;
  const [draft, setDraft] = useState(() =>
    compareMode ? pickBasics(result.inputs) : { ...DEFAULT_QUICK_DRAFT, ...quickDraft }
  );
  const [showErrors, setShowErrors] = useState(false);

  // Keep the draft across navigation (e.g. a detour to sign in)
  useEffect(() => {
    if (!compareMode) setQuickDraft(draft);
  }, [draft, compareMode, setQuickDraft]);

  const handleChange = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handleCalculate = () => {
    if (!isBasicsValid(draft)) {
      setShowErrors(true);
      return;
    }
    if (!checkAccess(GATE_ACTIONS.QUICK_ESTIMATE)) return;

    const estimate = calculateQuickEstimate({ ...draft, builtUpArea: Number(draft.builtUpArea) });
    if (compareMode) {
      setCompare(estimate);
    } else {
      setResult(estimate);
    }
    navigate('/app/estimate/result');
  };

  return (
    <Page maxWidth="md">
      <PageHeader
        title={t.quickTitle}
        subtitle={t.quickDesc}
        onBack={() => navigate(compareMode ? '/app/estimate/result' : '/app')}
        backLabel={t.back}
      />

      {compareMode ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t.comparing}: <strong>{result.inputs.projectName || t.current}</strong>
        </Alert>
      ) : (
        <UsageAlertBanner actionCode={GATE_ACTIONS.QUICK_ESTIMATE} language={language} />
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <HouseBasicsFields
            draft={draft}
            onChange={handleChange}
            t={t}
            language={language}
            showErrors={showErrors}
            showProjectName={!compareMode}
          />
        </CardContent>
      </Card>

      <StickyActions>
        <Button
          fullWidth
          size="large"
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={handleCalculate}
        >
          {compareMode ? t.calculateCompare : t.calculate}
        </Button>
      </StickyActions>

      <GateModalComponent />
    </Page>
  );
};

export default QuickEstimatePage;
